import { NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule, REMOVE_STYLES_ON_COMPONENT_DESTROY } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

import { RoutesModule } from './routes/routes.module'
import { AppComponent } from './app.component';

// HTTP
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

// Libraries
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// IMPORTS COMPONENTS

// SERVICES
import { ApiService } from './api/api.service';
import { LoaderService } from './api/loader.service';
import { IamService } from './services/iam.service';
import { MeService } from './services/me.service';
import { AuthService } from './services/auth.service';
import { ConfService } from './services/conf.service';
import { RequestService } from './api/request.service';
import { NotificationService } from './services/notification.service';
import { AuthGuardService } from './routes/guards/auth-guard.service';
import { CollectorService } from './services/collector.service';



// FORMS
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// PRIMENG
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker'
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

// SCHEMAS
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NavService } from './services/nav.service';
import { GmapGuardService } from './routes/guards/gmap-guard.service';
import { RolesGuardService } from './routes/guards/roles-guard.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AssistanceTicketsTypesService } from './components/assistances/assistance-tickets/assistance-tickets-types.service';
import { DistancecalculService } from './services/distancecalcul.service';
import { OrthographyService } from './services/orthography.service';
import { SponsorService } from './services/sponsor.service';
import { TranslateMessageService } from './services/translate-message.service';
import { UserService } from './services/user.service';
import { TrackedMessageService } from './services/tracked-message.service';
// import { TraceInterceptor } from './services/trace-interceptor.service';
import { TraceInterceptor } from './services/trace-interceptor.service';
import { DatePipe } from '@angular/common';
import { SharedModule } from './modules/shared-module/shared-module.module';
import { DrawerModule } from 'primeng/drawer';
import { AssistanceCasesModule } from './modules/assistance-cases/assistance-cases.module';
import { AssistanceTicketSvc } from './components/assistances/services/assistance-ticket.service';
//import { AssistanceReassignViewProviderComponent } from './components/assistances/assistance-tickets/assistance-reassign-view-provider/assistance-reassign-view-provider.component';
// import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { MatBadgeModule } from '@angular/material/badge';

import { MatTreeModule } from '@angular/material/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NodeService } from './components/medical/medical-category/node.service';
import { MedicalCaseCommonService } from './services/medical-case-common.service';
import { MaterialModule } from './material.module';

import { FirebaseService } from './services/firebase.service';
import { StorageService } from './services/storage.service';
import { ChatStateService } from './services/chat-state.service';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

const Apolo11Preset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    }
  }
});

import { DialogService } from 'primeng/dynamicdialog';
import { DynamicDialogModule } from 'primeng/dynamicdialog';

import { ApiInterceptorProvider } from './api/api.interceptor';
import { GoogleMapsModule } from '@angular/google-maps';
import { UsersProviderComponent } from './dashboard/management-app/users-provider/users-provider.component';
import { HistoryCompletedCasesComponent } from './dashboard/management-app/history-completed-cases/history-completed-cases.component';
import { AppLoaderComponent } from './components/app-loader/app-loader.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    AppLoaderComponent,
    MatFormFieldModule, MatInputModule,
    FormsModule,
    MatTreeModule,
    MatCheckboxModule,
    MatIconModule,
    MatChipsModule,
    ReactiveFormsModule,
    BrowserModule,
    MatDatepickerModule,
    MatNativeDateModule,
    RoutesModule,
    HttpClientModule,
    MaterialModule,
    GoogleMapsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    ToastModule,
    DialogModule,
    MatDialogModule,
    BrowserAnimationsModule,
    MatButtonModule,
    DrawerModule,
    DatePickerModule,

    AssistanceCasesModule,
    MatBadgeModule,
    MultiSelectModule,
    PaginatorModule,
    MessageModule,
    ConfirmDialogModule,
    DynamicDialogModule,
    SharedModule,
  ],
  providers: [
    provideZoneChangeDetection(),
    providePrimeNG({
      theme: {
        preset: Apolo11Preset,
        options: {
          darkModeSelector: false
        }
      }
    }),
    { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: false },
    {
      provide: 'FIREBASE_STORAGE',
      useFactory: (firebaseService: FirebaseService) => firebaseService.storage,
      deps: [FirebaseService]
    },
    ConfirmationService,
    ConfService,
    ApiService,
    RequestService,
    AuthService,
    MeService,
    IamService,
    AuthGuardService,
    RolesGuardService,
    GmapGuardService,
    AssistanceTicketSvc,
    AssistanceTicketsTypesService,
    DistancecalculService,
    LoaderService,
    OrthographyService,
    NavService,
    SponsorService,
    TranslateMessageService,
    NotificationService,
    UserService,
    DatePipe,
    MessageService,
    TrackedMessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TraceInterceptor,
      multi: true
    },
    NodeService,
    MedicalCaseCommonService,
    FirebaseService,
    StorageService,
    CollectorService,
    ChatStateService,
    DialogService,
    ApiInterceptorProvider
  ],
  bootstrap: [AppComponent],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ]
})
export class AppModule { }
